import type { AlgorithmOperation } from "@algoquest/schemas";
import { LinearSearchEngine, type LinearSearchDomainState } from "./linear-search";
import type { SearchActionOutcome, SearchController, SearchViewState } from "./SearchController";

function toView(state: LinearSearchDomainState): SearchViewState {
  return {
    mode: "linear-search",
    values: state.values,
    target: state.target,
    focusIndex: state.isComplete ? null : state.nextIndex,
    awaitingChoice: false,
    range: null,
    checkedIndices: state.checkedIndices,
    eliminatedRanges: [],
    foundIndex: state.foundIndex,
    comparisons: state.comparisons,
    mistakes: 0,
    isComplete: state.isComplete,
  };
}

/**
 * Wraps LinearSearchEngine behind the shared SearchController contract
 * (Phase 04). Linear Search has no direction-choice step, so
 * handleDirectionChoice is always a no-op — GameRuntime doesn't need to
 * know that; it just gets `{ kind: "ignored" }` back like it would for
 * any input that doesn't apply right now.
 */
export class LinearSearchController implements SearchController {
  readonly mode = "linear-search" as const;
  private readonly engine: LinearSearchEngine;

  constructor(values: number[], target: number) {
    this.engine = new LinearSearchEngine(values, target);
  }

  getView(): SearchViewState {
    return toView(this.engine.getState());
  }

  getTrace(): AlgorithmOperation[] {
    return this.engine.getTrace();
  }

  handlePrimaryAction(nearestIndex: number | null): SearchActionOutcome {
    const state = this.engine.getState();
    if (state.isComplete || nearestIndex === null) {
      return { kind: "ignored" };
    }
    if (state.nextIndex !== nearestIndex) {
      return { kind: "rejected-wrong-box", expectedIndex: state.nextIndex };
    }
    const result = this.engine.step(performance.now());
    if (!result) {
      return { kind: "ignored" };
    }
    return { kind: "accepted", result: { view: toView(result.state), operations: result.operations } };
  }

  handleDirectionChoice(): SearchActionOutcome {
    return { kind: "ignored" };
  }
}
